import { useState, useEffect } from 'react';
import { 
  Layout, 
  Menu, 
  Button, 
  Space, 
  Avatar, 
  Dropdown, 
  Typography,
  Breadcrumb 
} from 'antd';
import { 
  MenuUnfoldOutlined, 
  MenuFoldOutlined, 
  UserOutlined,
  DashboardOutlined,
  ShoppingOutlined,
  TeamOutlined,
  SettingOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const { Header } = Layout;
const { Text } = Typography;

interface AdminHeaderProps {
  collapsed: boolean;
  toggle: () => void;
}

export default function AdminHeader({ collapsed, toggle }: AdminHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  
  useEffect(() => {
    const getUserEmail = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user?.email) {
        setUserEmail(data.user.email);
      }
    };
    
    getUserEmail();
  }, []);
  
  // Generate breadcrumb items based on current path
  const generateBreadcrumb = () => {
    const pathSnippets = location.pathname.split('/').filter(i => i);
    
    const breadcrumbItems = pathSnippets.map((segment, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
      
      // Generate a more user-friendly title
      let title = segment.charAt(0).toUpperCase() + segment.slice(1);
      
      // Replace with Dutch translations for specific segments
      if (segment === 'admin') title = 'Admin';
      if (segment === 'products') title = 'Producten';
      if (segment === 'clients') title = 'Klanten';
      if (segment === 'companies') title = 'Bedrijven';
      if (segment === 'orders') title = 'Bestellingen';
      if (segment === 'settings') title = 'Instellingen';
      if (segment === 'leveranciers') title = 'Leveranciers';
      if (segment === 'new') title = 'Nieuw';
      if (segment === 'edit') title = 'Bewerken';
      if (segment === 'view') title = 'Bekijken';
      
      return {
        title: title,
        key: url,
      };
    });
    
    return [
      {
        title: 'Home',
        key: '/',
      },
      ...breadcrumbItems,
    ];
  };
  
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };
  
  const items = [
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/admin/settings'),
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];
  
  const breadcrumbItems = generateBreadcrumb();
  
  return (
    <Header style={{ 
      padding: '0 16px', 
      background: '#fff', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between', 
      boxShadow: '0 1px 4px rgba(0,21,41,.08)',
      position: 'sticky',
      top: 0,
      zIndex: 1,
      width: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={toggle}
          style={{ fontSize: '16px', width: 64, height: 64 }}
        />
        <Breadcrumb items={breadcrumbItems} />
      </div>
      
      <div>
        <Space>
          <Text type="secondary" style={{ marginRight: 8 }}>
            <strong>Admin:</strong> {userEmail || 'Loading...'}
          </Text>
          <Dropdown menu={{ items }} placement="bottomRight">
            <Avatar 
              style={{ backgroundColor: '#1890ff', cursor: 'pointer' }}
              icon={<UserOutlined />}
            />
          </Dropdown>
        </Space>
      </div>
    </Header>
  );
} 